/**
 * FunnelPage Component
 * 
 * Main orchestrator for the funnel flow.
 * Handles:
 * - Step navigation logic
 * - Question rendering based on type
 * - Answer state management
 * - Validation and error handling
 * - Progress tracking
 * 
 * This is the core component that ties everything together.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useFunnel } from '../context/FunnelContext';
import { useFunnelState } from '../hooks/useFunnelState';
import { useStepNavigation } from '../hooks/useStepNavigation';
import { ProgressBar } from './Layout/ProgressBar';
import { CompactFooter } from './Layout/Footer';
import { QuestionWrapper } from './Questions/QuestionWrapper';
import { TextQuestion } from './Questions/TextQuestion';
import { SelectQuestion } from './Questions/SelectQuestion';
import { ContactQuestion } from './Questions/ContactQuestion';
import { ThankYou } from './Results/ThankYou';
import { validateField, isValidEmail } from '../utils/validation';
import { ServiceType } from '../types/funnel.types';
import { trackFunnelStarted } from '../utils/analytics';

// ============================================
// COMPONENT
// ============================================

/**
 * FunnelPage - Main funnel interface
 */
export const FunnelPage: React.FC = () => {
  // Get funnel state and helpers
  const { setAnswer, setServiceType, state } = useFunnel();
  const {
    currentQuestion,
    currentAnswer,
    isFirstStep,
    isLastQuestionStep,
    isThankYouStep,
    accurateProgress,
    totalQuestions,
  } = useFunnelState();
  
  // Navigation helpers
  const { goNext, goBack, completeFunnel } = useStepNavigation();

  // Local state for current answer (before committing to context)
  const [localAnswer, setLocalAnswer] = useState<unknown>(currentAnswer);
  const [error, setError] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Terms agreement checkbox state (only for question 1)
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [termsError, setTermsError] = useState(false);

  // Track funnel start on mount
  useEffect(() => {
    trackFunnelStarted();
  }, []);

  // Sync local answer with context when step changes
  useEffect(() => {
    setLocalAnswer(currentAnswer);
    setError('');
    setFieldErrors({});
    setTermsAgreed(false);
    setTermsError(false);
  }, [currentAnswer, state.currentStep]);

  // Handle answer change
  const handleAnswerChange = useCallback((value: unknown) => {
    setLocalAnswer(value);
    setError(''); // Clear error when user makes changes
  }, []);

  // Set field error (for multi-field questions)
  const setFieldError = useCallback((field: string, errorMsg: string) => {
    setFieldErrors(prev => ({
      ...prev,
      [field]: errorMsg,
    }));
  }, []);

  // Core function to handle moving to next step
  // Can receive an explicit value (for auto-advance) or use localAnswer
  const handleNextWithValue = useCallback(async (explicitValue?: unknown) => {
    // Use explicit value if provided, otherwise fall back to localAnswer
    const valueToUse = explicitValue !== undefined ? explicitValue : localAnswer;

    // Validate the value
    if (!currentQuestion) return;

    // Check terms agreement for question 1
    if (currentQuestion.id === 'intro-challenge' && !termsAgreed) {
      setTermsError(true);
      return;
    }

    // For selection questions - check if something is selected
    if (currentQuestion.type === 'single-select' || currentQuestion.type === 'multi-select') {
      if (!valueToUse) {
        setError('Please select an option');
        return;
      }
    }

    // For text/textarea questions
    if (currentQuestion.type === 'text' || currentQuestion.type === 'textarea') {
      const result = validateField(valueToUse, currentQuestion.validation);
      if (!result.valid) {
        setError(result.message || 'Please provide a valid answer');
        return;
      }
    }

    // For multi-field questions (contact info)
    if (currentQuestion.type === 'multi-field' && currentQuestion.fields) {
      const contactData = valueToUse as Record<string, string> || {};
      let hasErrors = false;
      const newErrors: Record<string, string> = {};

      for (const field of currentQuestion.fields) {
        const fieldValue = contactData[field.id] || '';

        if (field.validation?.required && !fieldValue.trim()) {
          newErrors[field.id] = 'This field is required';
          hasErrors = true;
          continue;
        }

        if (field.type === 'email' && fieldValue && !isValidEmail(fieldValue)) {
          newErrors[field.id] = 'Please enter a valid email address';
          hasErrors = true;
          continue;
        }

        if (field.validation?.minLength && fieldValue.length < field.validation.minLength) {
          newErrors[field.id] = `Please enter at least ${field.validation.minLength} characters`;
          hasErrors = true;
        }
      }

      if (hasErrors) {
        setFieldErrors(newErrors);
        return;
      }
    }

    // Clear any existing errors
    setError('');
    setFieldErrors({});

    // Save the answer
    setAnswer(currentQuestion.id, valueToUse);

    // If this is the service type question, also set the service type
    if (currentQuestion.id === 'service-type' && valueToUse) {
      setServiceType(valueToUse as ServiceType);
    }

    // If this is the last question, submit and complete
    if (isLastQuestionStep) {
      setIsSubmitting(true);
      
      try {
        // Simulate API submission (replace with actual API call)
        await submitFunnelData();
        completeFunnel();
      } catch (err) {
        setError('Failed to submit. Please try again.');
        console.error('Submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Move to next step
      goNext(valueToUse);
    }
  }, [
    currentQuestion,
    localAnswer,
    termsAgreed,
    isLastQuestionStep,
    setAnswer,
    setServiceType,
    completeFunnel,
    goNext,
  ]);

  // Handle next button click (uses localAnswer from state)
  const handleNext = useCallback(async () => {
    await handleNextWithValue();
  }, [handleNextWithValue]);

  // Handle back button click
  const handleBack = useCallback(() => {
    // Save current answer before going back (optional behavior)
    if (currentQuestion && localAnswer) {
      setAnswer(currentQuestion.id, localAnswer);
    }
    goBack();
  }, [currentQuestion, localAnswer, setAnswer, goBack]);

  // Handle selection for auto-advance
  // Receives the selected value directly to avoid state timing issues
  const handleSelectionMade = useCallback((selectedValue?: unknown) => {
    // For single-select, auto-advance after selection
    if (currentQuestion?.type === 'single-select' && selectedValue) {
      // Small delay for visual feedback
      setTimeout(() => {
        // Pass the selected value directly to handleNext to avoid state timing issues
        handleNextWithValue(selectedValue);
      }, 200);
    }
  }, [currentQuestion, handleNextWithValue]);

  // Handle funnel restart
  const handleRestart = useCallback(() => {
    // The resetFunnel in ThankYou component handles this
    // This will reload to step 1
    window.location.reload();
  }, []);

  // Submit funnel data to webhook
  const submitFunnelData = async (): Promise<void> => {
    try {
      // Prepare the submission data
      const submissionData = {
        // All collected answers
        answers: state.answers,
        
        // Service type selected
        serviceType: state.serviceType,
        
        // Contact information (extracted for easy access)
        contactInfo: state.answers['contact-info'] as Record<string, string> || {},
        
        // Timestamps
        startedAt: state.startedAt,
        completedAt: new Date().toISOString(),
        
        // Completion time in seconds
        completionTime: state.startedAt 
          ? Math.round((Date.now() - new Date(state.startedAt).getTime()) / 1000)
          : 0,
        
        // User agent and browser info
        userAgent: navigator.userAgent,
        language: navigator.language,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        
        // Source tracking
        referrer: document.referrer || 'direct',
        url: window.location.href,
      };

      console.log('📤 Submitting to webhook...', submissionData);

      // Submit to webhook with correct URL
      await fetch('https://happythumbs.cloud/webhook-test/b0afd5f9-b43f-4ada-b2f3-7f95ff55645c', {
        method: 'POST',
        mode: 'no-cors', // This allows the request even if CORS headers aren't set
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      // With no-cors mode, we can't read the response, but the request will go through
      // Log success
      console.log('✅ Funnel data submitted to webhook successfully');
      console.log('📋 Submitted data:', submissionData);
      
      // Also log to console for easy copy-paste if needed
      console.table(submissionData.contactInfo);
      
    } catch (error) {
      // Log detailed error information
      console.error('❌ Error submitting to webhook:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
      });
      
      // Still log the data locally for debugging
      console.log('📋 Collected data (submission failed):', {
        answers: state.answers,
        serviceType: state.serviceType,
        contactInfo: state.answers['contact-info'],
      });
      
      // DON'T throw error - allow user to proceed to thank you page anyway
      // The data is logged to console and can be retrieved
      console.warn('⚠️ Proceeding to thank you page despite submission error');
    }
  };

  // Check if can proceed (has valid answer)
  const canProceed = (() => {
    if (!currentQuestion) return false;
    
    // For text questions, check min length
    if (currentQuestion.type === 'textarea' || currentQuestion.type === 'text') {
      const textValue = typeof localAnswer === 'string' ? localAnswer : '';
      const minLength = currentQuestion.validation?.minLength || 0;
      const hasMinLength = textValue.length >= minLength;
      
      // For question 1, also require terms agreement
      if (currentQuestion.id === 'intro-challenge') {
        return hasMinLength && termsAgreed;
      }
      
      return hasMinLength;
    }
    
    // For selection questions, check if selected
    if (currentQuestion.type === 'single-select') {
      return !!localAnswer;
    }
    
    // For multi-field, check required fields
    if (currentQuestion.type === 'multi-field' && currentQuestion.fields) {
      const contactData = localAnswer as Record<string, string> || {};
      return currentQuestion.fields
        .filter(f => f.validation?.required)
        .every(f => {
          const value = contactData[f.id] || '';
          if (f.type === 'email') return isValidEmail(value);
          return value.trim().length > 0;
        });
    }
    
    return true;
  })();

  // Render the thank you page if complete
  if (isThankYouStep) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500">
        {/* Progress bar at 100% */}
        <ProgressBar
          currentStep={totalQuestions + 1}
          totalSteps={totalQuestions + 1}
          progress={100}
          hideStepCounter
        />
        
        {/* Content container */}
        <main className="flex-grow flex items-center justify-center py-20">
          <ThankYou onRestart={handleRestart} />
        </main>
        
        <div className="relative z-10">
          <CompactFooter />
        </div>
      </div>
    );
  }

  // Render the question page
  if (!currentQuestion) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-text-secondary">Loading...</p>
      </div>
    );
  }

  // Check if this is question 1 (intro question)
  const isQuestion1 = currentQuestion.id === 'intro-challenge';

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500">
      {/* Progress bar */}
      <div className="relative z-20">
        <ProgressBar
          currentStep={state.currentStep}
          totalSteps={totalQuestions + 1}
          progress={accurateProgress}
        />
      </div>
      
      {/* Main content area */}
      <main className="relative z-10 flex-grow flex items-center justify-center py-20 px-4">
        <AnimatePresence mode="wait">
          <QuestionWrapper
            key={currentQuestion.id}
            question={currentQuestion}
            currentStep={state.currentStep}
            totalSteps={totalQuestions + 1}
            isFirst={isFirstStep}
            isLast={isLastQuestionStep}
            canProceed={canProceed}
            isLoading={isSubmitting}
            error={error}
            onNext={handleNext}
            onBack={handleBack}
            isQuestion1={isQuestion1}
            termsAgreed={termsAgreed}
            onTermsChange={(checked) => {
              setTermsAgreed(checked);
              if (checked) setTermsError(false);
            }}
            termsError={termsError}
          >
            {renderQuestionContent({
              question: currentQuestion,
              value: localAnswer,
              onChange: handleAnswerChange,
              onSelect: handleSelectionMade,
              error,
              setError,
              fieldErrors,
              setFieldError,
            })}
          </QuestionWrapper>
        </AnimatePresence>
      </main>
      
      <div className="relative z-10">
        <CompactFooter />
      </div>
    </div>
  );
};

// ============================================
// RENDER HELPERS
// ============================================

interface RenderQuestionProps {
  question: ReturnType<typeof useFunnelState>['currentQuestion'];
  value: unknown;
  onChange: (value: unknown) => void;
  onSelect: (selectedValue: unknown) => void;
  error: string;
  setError: (error: string) => void;
  fieldErrors: Record<string, string>;
  setFieldError: (field: string, error: string) => void;
}

/**
 * Render the appropriate question component based on question type
 */
function renderQuestionContent({
  question,
  value,
  onChange,
  onSelect,
  error,
  setError,
  fieldErrors,
  setFieldError,
}: RenderQuestionProps): React.ReactNode {
  if (!question) return null;

  switch (question.type) {
    case 'text':
    case 'textarea':
      return (
        <TextQuestion
          question={question}
          value={(value as string) || ''}
          onChange={(v) => onChange(v)}
          error={error}
          setError={setError}
        />
      );

    case 'single-select':
    case 'multi-select':
      return (
        <SelectQuestion
          question={question}
          value={value as string | string[] | undefined}
          onChange={(v) => onChange(v)}
          onSelect={(selectedValue) => onSelect(selectedValue)}
          autoAdvance={question.type === 'single-select'}
        />
      );

    case 'multi-field':
      return (
        <ContactQuestion
          question={question}
          value={(value as Record<string, string>) || {}}
          onChange={(v) => onChange(v)}
          errors={fieldErrors}
          setFieldError={setFieldError}
        />
      );

    default:
      return <p>Unknown question type</p>;
  }
}

FunnelPage.displayName = 'FunnelPage';

export default FunnelPage;
